/**
 * The handoff to Google: what waits here, and what is allowed to come back.
 *
 * Two problems, one mechanism.
 *
 * The OAuth `state` is the only value this app hands to accounts.google.com, and on the share routes
 * the path itself is the capability. Dropping such a path stopped the leak and took the return address
 * with it — a logged-out holder of a shared note who chose Google came back to the home page with the
 * note not added and nothing said, while the email login still worked.
 *
 * And `state` bound nothing: it was never sent to the server, so a returning `code` was accepted
 * whoever started the flow. An attacker holding a valid unredeemed code for their own account could
 * have a victim open one link and be signed in as them — the attacker's token in the victim's
 * `localStorage`, which is per-origin, so every tab of the app follows.
 *
 * Per-ORIGIN storage, never per-tab, and this is the whole point of the control. Session storage is
 * per TAB, and the attack is delivered as a link: it opens in a fresh tab that never held a handoff,
 * which is indistinguishable from a browser that refused to store one — so an allow-on-nothing-stored
 * rule fires for the victim and never for the attacker. Here a store that reads back empty is a
 * refusal, and only a browser with no reachable store at all is unverifiable.
 *
 * Two stores, tried in order: `localStorage`, then a short-lived cookie. Both are per-origin, so the
 * rule above holds for either. The second exists because the first throws outright in Safari with
 * third-party cookies blocked, in some private modes and in some iframe contexts — and "the store
 * threw" used to mean the code was exchanged with nothing checked, which is the exact CSRF hole this
 * module exists to close, left open for precisely the users whose browser could not be verified.
 * With a fallback those browsers are now verifiable like everyone else, and `unverifiable` — which
 * the callback refuses — is left for a browser that can hold nothing at all.
 */
const KEY = "pendingOAuthHandoff";

/** Long enough for a slow consent screen, short enough that an abandoned flow does not linger. */
const MAX_AGE_MS = 10 * 60 * 1000;

interface Handoff {
	nonce: string;
	path: string;
	at: number;
}

/**
 * A place to keep one short-lived value for this origin.
 *
 * `write` verifies by reading back: `document.cookie` is a silent no-op when cookies are blocked, so
 * an unchecked assignment would report success and leave the next return with nothing to match.
 */
interface HandoffStore {
	read: () => string | null;
	write: (value: string) => void;
	clear: () => void;
}

const localStore: HandoffStore = {
	read: () => localStorage.getItem(KEY),
	write: value => localStorage.setItem(KEY, value),
	clear: () => localStorage.removeItem(KEY),
};

const cookieStore: HandoffStore = {
	read: () => {
		const match = document.cookie.match(new RegExp(`(?:^|; )${KEY}=([^;]*)`));

		return match ? decodeURIComponent(match[1]) : null;
	},
	write: value => {
		const attributes = `path=/; max-age=${Math.floor(MAX_AGE_MS / 1000)}; SameSite=Lax`;
		document.cookie = `${KEY}=${encodeURIComponent(value)}; ${attributes}`;
		if (cookieStore.read() === null) throw new Error("cookies are not writable here");
	},
	clear: () => {
		document.cookie = `${KEY}=; path=/; max-age=0; SameSite=Lax`;
	},
};

const STORES: readonly HandoffStore[] = [localStore, cookieStore];

export type HandoffResult =
	/** This browser started this flow, and here is where it was going. */
	| { status: "ok"; path: string }
	/** Somebody else's return, or one this browser has no record of. Do not exchange the code. */
	| { status: "refused" }
	/** No store could answer, so the question cannot be asked at all — see `takeOAuthHandoff`. */
	| { status: "unverifiable" };

const randomNonce = (): string => {
	if (typeof crypto?.randomUUID === "function") return crypto.randomUUID();

	// Older Safari has getRandomValues without randomUUID. Guarded the same way as the line above: a
	// context with neither is one where no unguessable value can be produced at all, and a nonce that
	// is not unguessable is worse than an honest failure to make one.
	const bytes = new Uint8Array(16);
	crypto.getRandomValues(bytes);

	return Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
};

/**
 * Prepared at render because the `state` has to be in the link's href, but written only when the user
 * actually goes: building the href used to write the capability into storage the moment a login page
 * rendered, whether or not anyone chose Google, and nothing cleared it afterwards.
 */
export const prepareOAuthHandoff = (
	redirectPath: unknown,
): { state: string; remember: () => void } => {
	const nonce = randomNonce();
	const path = typeof redirectPath === "string" ? redirectPath : "/";

	return {
		state: JSON.stringify({ nonce }),
		remember: () => {
			const value = JSON.stringify({ nonce, path, at: Date.now() });

			for (const store of STORES) {
				try {
					store.write(value);

					return;
				} catch {
					// Try the next one. If none of them holds it, `takeOAuthHandoff` finds a readable
					// but empty store and refuses, which is the safe end of this trade.
				}
			}
		},
	};
};

/** Cleared everywhere, not just where it was written: a leftover in the other store still reads. */
export const clearOAuthHandoff = (): void => {
	for (const store of STORES) {
		try {
			store.clear();
		} catch {
			// Nothing to clear in a store that is refusing us.
		}
	}
};

/**
 * What a return from Google is worth.
 *
 * A mismatch does NOT consume the stored handoff: one attacker-triggered callback would otherwise
 * both degrade the next return to "nothing stored" and destroy the return address of the victim's own
 * sign-in, which is the silent failure this module exists to remove. Only a match spends it.
 */
export const takeOAuthHandoff = (rawState: unknown): HandoffResult => {
	let raw: string | null = null;
	let answered = false;

	for (const store of STORES) {
		try {
			const value = store.read();
			answered = true;
			if (value !== null) {
				raw = value;
				break;
			}
		} catch {
			// This store cannot answer; the next one may.
		}
	}

	// Not one store could be reached, so the question cannot be asked at all. The callback refuses
	// on this, same as on a mismatch — a browser that can hold nothing cannot prove it started the
	// flow, and exchanging the code anyway is the hole, not the fallback.
	if (!answered) return { status: "unverifiable" };

	// A value something else mangled is no record at all.
	let stored: Handoff | null;
	try {
		stored = raw ? (JSON.parse(raw) as Handoff) : null;
	} catch {
		stored = null;
	}

	if (!stored?.nonce) return { status: "refused" };

	if (!(Date.now() - stored.at < MAX_AGE_MS)) {
		clearOAuthHandoff();

		return { status: "refused" };
	}

	let nonce: unknown = null;
	try {
		nonce = typeof rawState === "string" ? JSON.parse(rawState).nonce : null;
	} catch {
		// A `state` this app did not write. Judged below like any other mismatch.
	}

	if (stored.nonce !== nonce) return { status: "refused" };

	clearOAuthHandoff();

	return { status: "ok", path: stored.path };
};
